import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];

const getVal = (x, ...keys) => {
  for (const k of keys)
    if (x?.[k] !== undefined && x?.[k] !== null) return x[k];
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 28, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  tableWrap: {
    overflowX: "auto",
    maxHeight: 420,
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0, minWidth: 1050 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: { fontSize: 10, padding: "4px 7px", borderRadius: 20 },
};

export default function BankReconciliationPage() {
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({
    accountId: "",
    fromDate: today(),
    toDate: today(),
    status: "false",
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const bankAccounts = useMemo(() => {
    return accounts.filter((a) =>
      String(getVal(a, "AccountName", "accountName"))
        .toLowerCase()
        .includes("bank"),
    );
  }, [accounts]);

  const loadAccounts = async () => {
    const res = await client.get("/accounts");
    setAccounts(Array.isArray(res.data) ? res.data : []);
  };

  const loadEntries = async () => {
    if (!filter.accountId) return alert("Select bank account");

    const statusQuery =
      filter.status === "all" ? "" : `&isReconciled=${filter.status}`;

    const res = await client.get(
      `/finance/bank-reconciliation?accountId=${Number(
        filter.accountId,
      )}&fromDate=${filter.fromDate}&toDate=${filter.toDate}${statusQuery}`,
    );

    setRows(res.data || []);
  };

  const reconcile = async (row) => {
    const journalDetailId = getVal(row, "JournalDetailId", "journalDetailId");
    const bankReferenceNo = prompt("Enter Bank Reference No / UTR", "");
    if (bankReferenceNo === null) return;

    await client.post("/finance/bank-reconciliation/reconcile", {
      journalDetailId: Number(journalDetailId),
      reconciledDate: today(),
      bankReferenceNo,
    });

    await loadEntries();
  };

  const unreconcile = async (row) => {
    const journalDetailId = getVal(row, "JournalDetailId", "journalDetailId");
    await client.post(
      `/finance/bank-reconciliation/unreconcile/${journalDetailId}`,
    );
    await loadEntries();
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>
            <i className="bi bi-check2-square me-2"></i>
            Bank Reconciliation
          </div>
          <div style={styles.sub}>
            Match bank transactions with ERP ledger entries
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <Field label="Bank Account" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.accountId}
                onChange={(e) =>
                  setFilter({ ...filter, accountId: e.target.value })
                }
              >
                <option value="">Select Bank</option>
                {bankAccounts.map((a) => {
                  const id = getVal(a, "AccountId", "accountId", "Id", "id");
                  const code = getVal(a, "AccountCode", "accountCode");
                  const name = getVal(a, "AccountName", "accountName");

                  return (
                    <option key={id} value={id}>
                      {code ? `${code} - ${name}` : name}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="From" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.fromDate}
                onChange={(e) =>
                  setFilter({ ...filter, fromDate: e.target.value })
                }
              />
            </Field>

            <Field label="To" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.toDate}
                onChange={(e) =>
                  setFilter({ ...filter, toDate: e.target.value })
                }
              />
            </Field>

            <Field label="Status" className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
              >
                <option value="false">Unreconciled</option>
                <option value="true">Reconciled</option>
                <option value="all">All</option>
              </select>
            </Field>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={loadEntries}
              >
                <i className="bi bi-search me-1"></i>
                Load
              </button>
            </div>
          </div>

          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              Entries: <strong>{rows.length}</strong>
            </span>
            <span>Reconciliation status</span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Voucher No",
                    "Description",
                    "Debit",
                    "Credit",
                    "Status",
                    "Bank Ref",
                    "Reconciled Date",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Debit", "Credit"].includes(h) ? "text-end" : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No entries
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const isRec = Boolean(
                      getVal(r, "IsReconciled", "isReconciled"),
                    );
                    const entryDate = getVal(r, "EntryDate", "entryDate");
                    const recDate = getVal(
                      r,
                      "ReconciledDate",
                      "reconciledDate",
                    );

                    return (
                      <tr key={i}>
                        <td style={styles.td}>
                          {entryDate
                            ? new Date(entryDate).toLocaleDateString("en-IN")
                            : ""}
                        </td>

                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {getVal(r, "VoucherNo", "voucherNo")}
                        </td>

                        <td style={styles.td}>
                          {getVal(r, "Description", "description")}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {num(getVal(r, "Debit", "debit")) > 0
                            ? num(getVal(r, "Debit", "debit")).toFixed(2)
                            : ""}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {num(getVal(r, "Credit", "credit")) > 0
                            ? num(getVal(r, "Credit", "credit")).toFixed(2)
                            : ""}
                        </td>

                        <td style={styles.td}>
                          <span
                            className={`badge ${
                              isRec ? "text-bg-success" : "text-bg-warning"
                            }`}
                            style={styles.badge}
                          >
                            {isRec ? "Reconciled" : "Pending"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {getVal(r, "BankReferenceNo", "bankReferenceNo")}
                        </td>

                        <td style={styles.td}>
                          {recDate
                            ? new Date(recDate).toLocaleDateString("en-IN")
                            : ""}
                        </td>

                        <td style={styles.td}>
                          {isRec ? (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              style={styles.btn}
                              onClick={() => unreconcile(r)}
                            >
                              Undo
                            </button>
                          ) : (
                            <button
                              className="btn btn-success btn-sm"
                              style={styles.btn}
                              onClick={() => reconcile(r)}
                            >
                              Reconcile
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
